import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import moment from "moment";
import { Icon } from "@iconify/react";

import {
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  InputAdornment,
  ListItemIcon,
  Menu,
  MenuItem,
  TextField,
} from "@mui/material";

import CloseOutlined from "@mui/icons-material/CloseOutlined";
import "./AddEditContactDialog.css";
import "../Components/Components.css";

import {
  addContactPhoneApproval,
  getCustomerDetail,
  getPhoneTypeList,
  upsertCustomer,
} from "../Store/Actions/ContactAction";

import { SET_IS_SELECTING_CONTACT } from "../Store/Types/AgentTypes";
import { UPDATE_CHAT_CONTACT_INFO } from "../Store/Types/ChatTypes";
import { GET_CONTACT_DETAILS } from "../Store/Types/ContactTypes";
import { GET_ASSIGNMENT_DETAILS } from "../Store/Types/OutboundTypes";

const AddEditContactDialog = ({
  contactID,
  fetchCustomer,
  isOpen,
  isAgent,
  setIsAddEditContactDialogShown,
  setIsContactDetailsModalShown,
  setSelected,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const { isInACW, phoneNumber } = useSelector((state) => state.call);

  const [fieldTypes, setFieldTypes] = useState([
    {
      id: 9,
      name: "Mobile",
      value: phoneNumber ? removePlusSign(phoneNumber) : "",
      isExisting: false,
    },
  ]);

  const [filteredPhoneTypeList, setFilteredPhoneTypeList] = useState([]);
  const [isHelpdeskContact, setIsHelpdeskContact] = useState(true); // Check if contact is a helpdesk contact
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [name, setName] = useState("");
  const [phoneTypeList, setPhoneTypeList] = useState([]);
  const [ref, setRef] = useState("");
  const [type, setType] = useState("");
  const dispatch = useDispatch();

  const resetFields = () => {
    setName("");
    setRef("");
    setType("");
    setIsHelpdeskContact(true);

    setFieldTypes([
      {
        id: 9,
        name: "Mobile",
        value: phoneNumber ? removePlusSign(phoneNumber) : "",
        isExisting: false,
      },
    ]);
  };

  // Get customer detail then map the data. This function only runs when editing
  useEffect(() => {
    resetFields();

    getPhoneTypeList(contactID)
      .then((response) => {
        if (response.status) {
          setPhoneTypeList(response.data);
        } else {
          console.log("error", response.error);
          toast.error("Error fetching phone type list", { autoClose: 3000 });
        }
      })
      .catch((err) => {
        console.log("err", err);
        toast.error("Error fetching phone type list", { autoClose: 3000 });
      });

    if (contactID) {
      getCustomerDetail(contactID)
        .then((response) => {
          if (response.status) {
            const details = response.data[0][0];
            const contacts = response.data[1];
            setName(details.ContactName);
            setRef(details.ContactRef);

            if (details.HelpdeskContact) {
              // If contact is helpdesk...
              details.CustomerType && setType(details.CustomerType); // ...set customer type (if it has a value)
            } else {
              setIsHelpdeskContact(false); // Else, hide customer type field...
              setType("Non Customer"); // ...and set customer type to 'Non Customer'
            }

            // Map contact list
            setFieldTypes(
              contacts?.map((contact) => {
                return {
                  id: contact.PhoneTypeID,
                  name: contact.PhoneType,
                  value: contact.PhoneNo,
                  isExisting: true,
                };
              })
            );
          } else {
            console.log("error", response.error);

            toast.error("Error fetching contact data", { autoClose: 3000 });

            handleClose();
          }
        })
        .catch((err) => {
          console.log("err", err);

          toast.error("Error fetching contact data", { autoClose: 3000 });

          handleClose();
        });
    }
  }, [isOpen]);

  // Filter phone types that has been selected
  useEffect(() => {
    setFilteredPhoneTypeList(
      phoneTypeList.filter(
        (phoneType) =>
          fieldTypes.findIndex(
            (fieldType) => fieldType.id === phoneType.PhoneTypeID
          ) === -1
      )
    );
  }, [fieldTypes]);

  useEffect(() => {
    if (phoneNumber) {
      setFieldTypes([
        {
          id: 9,
          name: "Mobile",
          value: removePlusSign(phoneNumber),
          isExisting: false,
        },
      ]);
    }
  }, [phoneNumber]);

  const open = Boolean(anchorEl);

  const handleClose = () => {
    resetFields();
    setIsAddEditContactDialogShown(false);
  };

  const handleAddField = (e) => {
    setAnchorEl(e.currentTarget);
    renderAddFieldMenuItems();
  };
  const handleCloseAddField = () => setAnchorEl(null);

  const handleFieldChange = (value, index) => {
    let currentFieldTypes = [...fieldTypes];
    currentFieldTypes[index].value = value;
    setFieldTypes(currentFieldTypes);
  };

  const handleRemoveField = (index) => {
    let currentFieldTypes = [...fieldTypes];
    currentFieldTypes.splice(index, 1);
    setFieldTypes(currentFieldTypes);
  };

  const renderAddFieldMenuItems = () => {
    return filteredPhoneTypeList.map((phoneType, index) => {
      const renderIcon = () => {
        switch (phoneType.Type) {
          case "PSTN      ":
            return "ic:round-other-houses";
          case "FAX       ":
            return "material-symbols:fax-outline-rounded";
          case "CELL      ":
            return "material-symbols:phone-iphone-outline";
          case "EMAIL     ":
            return "material-symbols:mail-outline";
          case "TWITTER   ":
            return "mdi:twitter";
          case "FACEBOOK  ":
            return "mdi:facebook";
          case "INSTAGRAM ":
            return "ri:instagram-line";
          default:
            break;
        }
      };

      return (
        <MenuItem
          key={index}
          onClick={() => {
            setFieldTypes((current) => [
              ...current,
              {
                id: phoneType.PhoneTypeID,
                name: phoneType.Name,
                value: "",
                isExisting: false,
              },
            ]);

            handleCloseAddField();
          }}
        >
          <Icon icon={renderIcon()} width="26" height="26" />
          {phoneType.Name}
        </MenuItem>
      );
    });
  };

  const renderFieldTypes = () => {
    let renderedFieldTypes = [];
    let icon, isPhoneNumber;

    fieldTypes.forEach((fieldType, index) => {
      switch (fieldType.id) {
        // PSTN
        case 1:
        case 2:
        case 3:
        case 7:
        case 8:
        case 11:
        case 12:
        case 13:
        case 14:
        case 15:
        case 16:
        case 17:
        case 18:
        case 19:
        case 20:
          icon = "ic:round-other-houses";
          isPhoneNumber = true;
          break;

        // FAX
        case 4:
        case 5:
          icon = "material-symbols:fax-outline-rounded";
          isPhoneNumber = true;
          break;

        // CELL
        case 6:
        case 9:
        case 10:
        case 21:
        case 22:
        case 23:
        case 24:
        case 25:
        case 26:
        case 27:
        case 28:
        case 29:
        case 30:
        case 31:
          icon = "material-symbols:phone-iphone-outline";
          isPhoneNumber = true;
          break;

        // EMAIL
        case 32:
          icon = "material-symbols:mail-outline";
          isPhoneNumber = false;
          break;

        // TWITTER
        case 33:
          icon = "mdi:twitter";
          isPhoneNumber = false;
          break;

        // FACEBOOK
        case 34:
          icon = "mdi:facebook";
          isPhoneNumber = false;
          break;

        // INSTAGRAM
        case 35:
          icon = "ri:instagram-line";
          isPhoneNumber = false;
          break;

        default:
          break;
      }

      renderedFieldTypes.push(
        <div className="item" key={index}>
          <TextField
            required={fieldTypes.length === 1 || index === 0}
            fullWidth
            inputProps={
              isPhoneNumber
                ? {
                    inputMode: "tel",
                    pattern: "[0-9]*",
                  }
                : ""
            }
            label={fieldType.name}
            placeholder={fieldType.name}
            type={fieldType.id === 32 ? "email" : ""}
            value={fieldType.value}
            InputProps={{
              readOnly: isAgent && fieldType.isExisting ? true : false,

              endAdornment: (!isAgent || !fieldType.isExisting) && (
                <InputAdornment
                  position="end"
                  sx={{ cursor: "pointer" }}
                  onClick={() => handleFieldChange("", index)}
                >
                  <Icon icon="ph:x-bold" width="26" />
                </InputAdornment>
              ),

              startAdornment: (
                <InputAdornment position="start">
                  <Icon icon={icon} width="26" height="26" />
                </InputAdornment>
              ),
            }}
            onChange={(e) => handleFieldChange(e.target.value, index)}
          />

          {(!isAgent || !fieldType.isExisting) && fieldTypes.length > 1 && (
            <IconButton
              className="remove-field-button"
              onClick={() => handleRemoveField(index)}
            >
              <Icon
                icon="ic:sharp-remove-circle"
                color="#727272"
                width="30"
                height="30"
              />
            </IconButton>
          )}
        </div>
      );
    });

    return renderedFieldTypes;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (isAgent) {
      // For agents (needs approval)
      const request = {
        contactid: contactID,
        createdtime: moment().format("YYYY-MM-DD HH:mm:ss"),

        contactphone: fieldTypes
          .filter((field) => !field.isExisting)
          .map((field) => {
            return {
              phoneno: field?.value?.trim(),
              phonetype: field.name,
              status: "new",
            };
          }),
      };

      addContactPhoneApproval(request)
        .then((response) => {
          if (response.status) {
            handleClose();
            dispatch({ type: UPDATE_CHAT_CONTACT_INFO });

            dispatch({
              type: SET_IS_SELECTING_CONTACT,
              payload: true,
            });

            toast.success(
              <>
                <p>Request Submitted</p>
                <p>Contact: {name}</p>
              </>,

              { autoClose: 3000 }
            );

            setIsSubmitting(false);
          } else {
            console.log("error", response.error);
            toast.error("Error submitting request", { autoClose: 3000 });
            setIsSubmitting(false);
          }
        })
        .catch((err) => {
          console.log("err", err);
          toast.error("Error submitting request", { autoClose: 3000 });
        });
    } else {
      // For supervisors (no approval needed)
      const contact = {
        name: name?.trim(),
        contactid: contactID ? contactID : 0,
        contactref: ref?.trim(),
        customertype: type,

        contactphone: fieldTypes.map((field) => {
          return {
            contactid: contactID ? contactID : 0,
            phoneno: field.value?.trim(),
            phonetype: field.name,
          };
        }),
      };

      upsertCustomer(contact)
        .then((response) => {
          if (response.status) {
            console.log("Output contactID", response.data.Output_ContactID);
            handleClose();
            dispatch({ type: UPDATE_CHAT_CONTACT_INFO });

            dispatch({
              type: SET_IS_SELECTING_CONTACT,
              payload: true,
            });

            toast.success(
              contactID
                ? "Contact updated successfully"
                : "Contact added successfully.",
              { autoClose: 3000 }
            );

            setIsSubmitting(false);
            dispatch({ type: GET_CONTACT_DETAILS });
            dispatch({ type: GET_ASSIGNMENT_DETAILS });
            fetchCustomer && fetchCustomer(removePlusSign(phoneNumber));

            if (isInACW) {
              setSelected && setSelected(response.data.Output_ContactID);
              setIsContactDetailsModalShown &&
                setIsContactDetailsModalShown(true);
            }
          } else {
            console.log("error", response.error);
            handleClose();

            toast.error(
              contactID ? "Error updating contact" : "Error adding contact.",
              { autoClose: 3000 }
            );

            setIsSubmitting(false);
          }
        })
        .catch((err) => {
          console.log("err", err);
          handleClose();

          toast.error(
            contactID ? "Error updating contact" : "Error adding contact.",
            { autoClose: 3000 }
          );

          setIsSubmitting(false);
        });
    }
  };

  function removePlusSign(number) {
    if (number.startsWith("+")) {
      return number.slice(1);
    }

    return number;
  }

  return (
    <Dialog
      open={isOpen}
      className="dialog add-edit-contact"
      fullWidth
      maxWidth="md"
      scroll="paper"
      onClose={handleClose}
    >
      <div className="dialog-container">
        <form method="post" onSubmit={handleSubmit}>
          <div className="dialog-content">
            <div className="header-container">
              <h2 className="title">
                {contactID ? "Edit Contact" : "Add New Contact"}
              </h2>

              <IconButton className="close-button" onClick={handleClose}>
                <CloseOutlined sx={{ fontSize: 28 }} />
              </IconButton>
            </div>
            <div className="form-container">
              <section className="form-inputs">
                {!isAgent && (
                  <>
                    <div className="item">
                      <TextField
                        autoComplete="off"
                        fullWidth
                        label="Contact Name"
                        name="name"
                        placeholder="Contact Name"
                        required
                        value={name}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment
                              position="end"
                              sx={{ cursor: "pointer" }}
                              onClick={() => setName("")}
                            >
                              <Icon icon="ph:x-bold" width="26" />
                            </InputAdornment>
                          ),

                          startAdornment: (
                            <InputAdornment position="start">
                              <Icon
                                icon="carbon:user-avatar-filled"
                                width="26"
                              />
                            </InputAdornment>
                          ),
                        }}
                        onChange={(e) => setName(e.target.value)}
                      />
                    </div>

                    {isHelpdeskContact && (
                      <div className="item type">
                        <TextField
                          fullWidth
                          label="Customer Type"
                          name="type"
                          placeholder="Customer Type"
                          required
                          select
                          value={type}
                          onChange={(e) => setType(e.target.value)}
                        >
                          <MenuItem value="Customer">
                            <ListItemIcon>
                              <Icon icon="mdi:user" width="28" height="28" />
                            </ListItemIcon>
                            Customer
                          </MenuItem>

                          <MenuItem value="Non Customer">
                            <ListItemIcon>
                              <Icon
                                icon="mdi:hand-extended"
                                width="28"
                                height="28"
                              />
                            </ListItemIcon>
                            Non Customer
                          </MenuItem>
                        </TextField>
                      </div>
                    )}

                    {type === "Customer" && (
                      <div className="item">
                        <TextField
                          autoComplete="off"
                          fullWidth
                          label="Contact Ref"
                          name="ref"
                          placeholder="Contact Ref"
                          required
                          value={ref}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment
                                position="end"
                                sx={{ cursor: "pointer" }}
                                onClick={() => setRef("")}
                              >
                                <Icon icon="ph:x-bold" width="26" />
                              </InputAdornment>
                            ),
                          }}
                          onChange={(e) => setRef(e.target.value)}
                        />
                      </div>
                    )}
                  </>
                )}

                <div className="add-field">
                  <hr />

                  <Button
                    variant="outlined"
                    className="add-field-button"
                    startIcon={<Icon icon="material-symbols:add-rounded" />}
                    onClick={handleAddField}
                  >
                    Add Field
                  </Button>

                  <Menu
                    anchorEl={anchorEl}
                    anchorOrigin={{
                      horizontal: 100,
                      vertical: 50,
                    }}
                    className="phone-type-menu"
                    id="Filter_menu"
                    open={open}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                    onClose={handleCloseAddField}
                  >
                    {renderAddFieldMenuItems()}
                  </Menu>
                </div>

                {renderFieldTypes()}
              </section>
            </div>
            <section className="buttons">
              <button className="cancel" type="button" onClick={handleClose}>
                Cancel
              </button>

              <button className="confirm" disabled={isSubmitting} type="submit">
                {isSubmitting && <CircularProgress className="progress" />}
                Confirm
              </button>
            </section>
          </div>
        </form>
      </div>
    </Dialog>
  );
};

export default AddEditContactDialog;
